import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminGlobalSearch } from './useAdmin';
import type { GlobalSearchHit } from './adminApi';
import './GlobalSearchBar.css';

const HIT_PATH: Record<GlobalSearchHit['type'], (id: string) => string> = {
  user: (id) => `/admin/users/${id}`,
  classroom: (_id) => `/admin/classrooms`,
  submission: (_id) => `/admin/submissions`,
  published_app: (_id) => `/admin/published-apps`,
};

const HIT_LABEL: Record<GlobalSearchHit['type'], string> = {
  user: 'User',
  classroom: 'Classroom',
  submission: 'Submission',
  published_app: 'Published app',
};

/**
 * Global search bar in the admin topbar (F19). Type a few chars,
 * see live results spanning users, classrooms, submissions, and
 * published apps. Click a hit to jump.
 */
export function AdminGlobalSearchBar(): JSX.Element {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useAdminGlobalSearch(debounced, debounced.length > 0);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const hits = data?.hits ?? [];

  return (
    <div className="bl-admin-search" ref={containerRef}>
      <input
        type="search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => q && setOpen(true)}
        placeholder="Search users, classrooms, apps, or paste a UUID…"
        aria-label="Global search"
        className="bl-admin-search__input"
      />
      {open && debounced.length > 0 && (
        <div className="bl-admin-search__panel" role="listbox">
          {isFetching && <p className="bl-admin-search__notice">Searching…</p>}
          {!isFetching && hits.length === 0 && (
            <p className="bl-admin-search__notice">No matches.</p>
          )}
          {hits.map((h) => (
            <button
              key={`${h.type}-${h.id}`}
              type="button"
              role="option"
              aria-selected={false}
              className="bl-admin-search__hit"
              onClick={() => {
                navigate(HIT_PATH[h.type](h.id));
                setOpen(false);
                setQ('');
              }}
            >
              <span className="bl-admin-search__hit-type">{HIT_LABEL[h.type]}</span>
              <span className="bl-admin-search__hit-label">{h.label}</span>
              {h.sublabel && <span className="bl-admin-search__hit-sub">{h.sublabel}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
